import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../components/ui";
import FormInput from "../../components/ui/FormInput";
import { otherDetailsFields } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../../components/shared/ErrorMessage";

const OtherDetails = ({
  customerId,
  otherDetailsData,
  setOtherDetailsData,
  handleNext,
  type,
}) => {
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = React.useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    defaultValues: otherDetailsData,
  });

  useEffect(() => {
    if (otherDetailsData) {
      Object.keys(otherDetailsData).forEach((key) => {
        setValue(key, otherDetailsData[key]);
      });
    }
  }, [otherDetailsData, setValue]);

  const onSubmit = async (data) => {
    console.log("Form Data:", JSON.stringify(data));

    try {
      // Insert or update is decided by WHETHER THIS SECTION EXISTS, not by the
      // URL's `type`. That flag describes the customer; this tab is optional, so
      // a customer edited here may never have had a row at all.
      //
      // It used to send type:"Edit" whenever the customer was in Edit mode, with
      // no id — `...data` carries one only when the form was populated from a
      // saved row. The server requires an id on Edit, so every customer who
      // skipped this step got "Update requires a valid ID." and could never fill
      // it in. That is 13,284 of 13,625 customers on the live database.
      const existingId = otherDetailsData?.id;
      const isNew = !existingId;

      const payload = {
        ...data,
        model: "other_details",
        type: isNew ? "Add" : "Edit",
      };

      if (isNew) {
        // Required, or the new row is orphaned with no customer.
        payload.customerId = customerId;
      } else {
        payload.id = existingId;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        payload
      );

      if (response.status === 201) {
        setOtherDetailsData(response.data.savedData);
        console.log("Other Details added successfully: ", response.data);
        // Navigation follows the WIZARD's mode, deliberately still `type` and not
        // `isNew`: in the Add wizard the operator advances to the next step, and
        // when editing a customer they return to the View screen — regardless of
        // whether this particular section had to be created or updated.
        if (type === "Add") {
          handleNext();
        }
        if (type === "Edit") {
          navigate("/addEditCustomer", {
            state: { type: "View", stepNo: 3, customerId },
          });
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error adding other details");
      }
    } catch (error) {
      console.error("Error Adding Other Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error adding other details";
      setErrorMsg(msg);
    }
  };
  const onError = (errors) => {
    // Extract error messages from the errors object
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message; // Extract only the error message
      return acc;
    }, {});

    // Log or process the error messages
    console.log("Form errors:", errorMessages);
  };

  return (
    <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden p-8 max-w-6xl mx-auto mt-6">
      <div className="flex items-start gap-3.5 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.13c1.434-1.25 3.799-1.25 5.233 0a2.625 2.625 0 0 1 0 3.82c-.727.635-1.507 1.061-2.207 1.458-.696.395-1.285.73-1.688 1.118a.75.75 0 1 1-1.042-1.08c.6-.577 1.286-.968 1.93-1.334.697-.396 1.37-.78 1.93-1.27a1.125 1.125 0 0 0 0-1.636ZM12 15.75a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-ink-900">Other Details</h2>
          <p className="text-xs text-ink-500 mt-0.5">Enter general supplementary customer details</p>
        </div>
        {type === "View" && (
          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              navigate("/addEditCustomer", {
                state: { type: "Edit", stepNo: 3, customerId },
              })
            }
          >
            Edit Details
          </Button>
        )}
      </div>
      <ErrorMessage message={errorMsg} />
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 mb-8">
          {otherDetailsFields.map((field, index) => (
            <div key={index} className="col-span-1">
              <FormInput
                label={field.label}
                type={field.type}
                name={field.name}
                register={register}
                watch={watch}
                control={control}
                errors={errors}
                options={field.options || []}
                required={field.required}
                placeholder={field.placeholder}
                readOnly={type === "View" || false}
              />
            </div>
          ))}
        </div>
        {!(type === "View") && (
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-border-light">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                if (type === "Edit") {
                  navigate("/addEditCustomer", {
                    state: { type: "View", stepNo: 3, customerId },
                  });
                } else {
                  navigate("/customers");
                }
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {type === "Edit" ? "Update" : "Save & Next"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default OtherDetails;
